import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@design-system';
import { Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { CARRIER_CATALOG } from '@shared/underwriting';
import { SheetModal } from '@/components/SheetModal';
import {
  useCreateWizardEvalRule,
  useDeleteWizardEvalRule,
  useUpdateWizardEvalRule,
} from '@/features/wizard-evals/hooks/use-wizard-evals';
import type { WizardEvalRule } from '@/features/wizard-evals/api/wizard-evals-api';

interface RuleDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: WizardEvalRule | null;
}

export function RuleDetailSheet({ open, onOpenChange, rule }: RuleDetailSheetProps) {
  const isEdit = !!rule;

  const [ruleId, setRuleId] = useState('');
  const [carrier, setCarrier] = useState('nationwide');
  const [category, setCategory] = useState('');
  const [ruleText, setRuleText] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setRuleId(rule?.ruleId ?? '');
    setCarrier(rule?.carrier ?? 'nationwide');
    setCategory(rule?.category ?? '');
    setRuleText(rule?.ruleText ?? '');
    setKeywords(rule?.keywords ?? []);
    setKeywordDraft('');
    setIsActive(rule?.isActive ?? true);
  }, [open, rule]);

  const commitKeywordDraft = () => {
    const trimmed = keywordDraft.trim();
    if (!trimmed) return;
    const parts = trimmed
      .split(/[,\n]/)
      .map(p => p.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    setKeywords(prev => {
      const seen = new Set(prev);
      const next = [...prev];
      for (const p of parts) {
        const key = p.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          next.push(p);
        }
      }
      return next;
    });
    setKeywordDraft('');
  };

  const removeKeyword = (kw: string) => {
    setKeywords(prev => prev.filter(k => k !== kw));
  };

  const createMutation = useCreateWizardEvalRule();
  const updateMutation = useUpdateWizardEvalRule(rule?.ruleId ?? '');
  const deleteMutation = useDeleteWizardEvalRule();

  const pending = createMutation.isPending || updateMutation.isPending;
  const deleting = deleteMutation.isPending;

  const handleSubmit = async () => {
    const trimmedRuleId = ruleId.trim();
    const trimmedCarrier = carrier.trim();
    const trimmedRuleText = ruleText.trim();
    const trimmedCategory = category.trim();

    if (!trimmedRuleText || !trimmedCarrier) {
      toast.error('Carrier and rule text are required');
      return;
    }
    if (!isEdit && !trimmedRuleId) {
      toast.error('Rule ID is required');
      return;
    }

    const pendingDraft = keywordDraft.trim();
    const finalKeywords = pendingDraft
      ? [...keywords, ...pendingDraft.split(/[,\n]/).map(p => p.trim()).filter(Boolean)]
      : keywords;
    const dedupedKeywords = Array.from(
      finalKeywords.reduce((acc, k) => {
        if (!acc.has(k.toLowerCase())) acc.set(k.toLowerCase(), k);
        return acc;
      }, new Map<string, string>()).values(),
    );

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          carrier: trimmedCarrier,
          category: trimmedCategory || null,
          ruleText: trimmedRuleText,
          keywords: dedupedKeywords,
          isActive,
        });
        toast.success('Rule saved');
      } else {
        await createMutation.mutateAsync({
          ruleId: trimmedRuleId,
          carrier: trimmedCarrier,
          category: trimmedCategory || null,
          ruleText: trimmedRuleText,
          keywords: dedupedKeywords,
          isActive,
        });
        toast.success('Rule created');
      }
      onOpenChange(false);
    } catch (err: any) {
      toast.error(isEdit ? 'Failed to save' : 'Failed to create', {
        description: err?.message ?? 'Unknown error',
      });
    }
  };

  const handleDelete = async () => {
    if (!rule) return;
    try {
      await deleteMutation.mutateAsync(rule.ruleId);
      toast.success('Rule deleted', {
        description: `${rule.ruleId} was removed.`,
      });
      onOpenChange(false);
    } catch (err: any) {
      toast.error('Failed to delete', {
        description: err?.message ?? 'Unknown error',
      });
    }
  };

  return (
    <SheetModal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? 'Edit rule' : 'New rule'}
      subTitle={
        isEdit
          ? 'Update the rule catalog. Changes apply to future runs and re-score prior runs on read.'
          : 'Add a rule to the catalog. Rule IDs must be unique across all carriers.'
      }
      className="sm:min-w-[85%] md:min-w-[520px] lg:min-w-[640px]"
    >
      <div className="flex flex-col gap-5 pb-6 pt-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor="rule-id">
            Rule ID
            {isEdit && <span className="ml-1 text-gray-600">(locked after creation)</span>}
          </Label>
          <Input
            id="rule-id"
            value={ruleId}
            onChange={e => setRuleId(e.target.value)}
            disabled={isEdit}
            placeholder="e.g. nationwide.cognitive.dementia"
            className="font-mono text-xs"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label>Carrier</Label>
            <Select
              value={carrier}
              onValueChange={setCarrier}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CARRIER_CATALOG.map(c => (
                  <SelectItem
                    key={c.name}
                    value={c.name}
                  >
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="rule-category">
              Category <span className="text-gray-600">(optional)</span>
            </Label>
            <Input
              id="rule-category"
              value={category}
              onChange={e => setCategory(e.target.value)}
              placeholder="e.g. cognitive"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="rule-text">Rule text</Label>
          <Textarea
            id="rule-text"
            value={ruleText}
            onChange={e => setRuleText(e.target.value)}
            rows={4}
            placeholder="The carrier-facing description of when this rule fires."
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="rule-keywords">
            Keywords{' '}
            <span className="text-gray-600">
              (used by the resolver to map LLM free-text references to this rule)
            </span>
          </Label>
          <div className="flex flex-wrap items-center gap-1 rounded border border-gray-300 bg-white p-2">
            {keywords.map(kw => (
              <span
                key={kw}
                className="bg-primary-50 text-primary-dark inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs"
              >
                <span className="font-mono">{kw}</span>
                <button
                  type="button"
                  onClick={() => removeKeyword(kw)}
                  className="hover:text-primary rounded-full"
                  aria-label={`Remove ${kw}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              id="rule-keywords"
              value={keywordDraft}
              onChange={e => setKeywordDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitKeywordDraft();
                } else if (
                  e.key === 'Backspace' &&
                  keywordDraft === '' &&
                  keywords.length > 0
                ) {
                  e.preventDefault();
                  setKeywords(prev => prev.slice(0, -1));
                }
              }}
              onBlur={commitKeywordDraft}
              placeholder={
                keywords.length === 0
                  ? 'Type a keyword and press Enter (or comma). e.g. alzheimer, dementia'
                  : 'Add another…'
              }
              className="min-w-[12rem] flex-1 border-none bg-transparent text-xs outline-none placeholder:text-gray-400"
            />
          </div>
          <p className="text-xs text-gray-600">
            Lowercase, substring matches against the LLM&rsquo;s free-text rule references. Keep
            them specific to this rule.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="rule-active"
            checked={isActive}
            onCheckedChange={checked => setIsActive(checked === true)}
          />
          <label
            htmlFor="rule-active"
            className="text-sm text-gray-700"
          >
            Active — include in the catalog the pipeline can flag
          </label>
        </div>

        {isEdit && rule && (
          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
            <dl className="grid grid-cols-2 gap-2">
              <dt className="text-gray-600">Created by</dt>
              <dd className="font-mono">{rule.createdBy}</dd>
              <dt className="text-gray-600">Created</dt>
              <dd>{new Date(rule.createdDateTime).toLocaleString()}</dd>
              <dt className="text-gray-600">Last updated</dt>
              <dd>{new Date(rule.updatedDateTime).toLocaleString()}</dd>
            </dl>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-gray-200 pt-4">
          <div>
            {isEdit && rule && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                    disabled={deleting}
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    {deleting ? 'Deleting…' : 'Delete'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
                    <AlertDialogDescription>
                      <span className="font-mono text-gray-900">{rule.ruleId}</span> will be
                      removed from the catalog. Test cases that reference it will still keep the
                      ID in their expected/unexpected lists, but the rule will no longer appear in
                      the catalog or in rule pickers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={pending}
            >
              {pending
                ? isEdit
                  ? 'Saving…'
                  : 'Creating…'
                : isEdit
                  ? 'Save changes'
                  : 'Create rule'}
            </Button>
          </div>
        </div>
      </div>
    </SheetModal>
  );
}
